import React, { Fragment } from "react";
import { STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import startCase from "lodash/startCase";
import { useFormikContext } from "formik";
import {
  SETUP_FUTURE_USAGE_COPY,
  SETUP_FUTURE_USAGE,
  SETUP_FUTURE_USAGE_OPTIONS,
  SETUP_FUTURE_USAGE_LABEL
} from "utils/constants/payment";
import Toggle from "react-toggle";
import { getToggleProps } from "utils/form";
import ExternalLink from "components/ExternalLink";
import { STRIPE_DOCS_LINKS } from "utils/constants/stripe";
import MultiSelect from "components/Form/fields/MultiSelect";
import MultiselectOption from "components/MultiselectOption";
import { useShortLinkSetupFutureUsage } from "utils/hooks/shortLink";
import InheritenceDisplay from "components/InheritenceDisplay";
import { omitDeep } from "utils/data";
import classnames from "classnames";

/**
 * Only set payment_intent_data.setup_future_usage: 'off_session'
 * The default is 'on_session' so we dont need to set it
 */
const DEFAULT_TITLE = "Save payment method";
const SetupFutureUsage = (
  { name, inherited, title } = { title: DEFAULT_TITLE }
) => {
  const ctxTitle = title || DEFAULT_TITLE;
  const {
    values,
    touched,
    setValues,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const {
    setupFutureUsage,
    error,
    inheritedSetupFutureUsage,
    activeInheritence,
    disableSetupFutureUsage,
    notice
  } = useShortLinkSetupFutureUsage({
    inherited
  });

  if (activeInheritence) {
    return (
      <InheritenceDisplay
        error={error}
        title={ctxTitle}
        onRemove={() => {
          setFieldValue(
            STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE,
            SETUP_FUTURE_USAGE.OFF_SESSION
          );
        }}
      >
        <div className="flex flex-row justify-between items-center pt3">
          <div className="f6 word-break">
            {startCase(inheritedSetupFutureUsage)}
          </div>
        </div>
      </InheritenceDisplay>
    );
  } else {
    const setupFutureUsageSelectProps = {
      value: setupFutureUsage
        ? [
            {
              id: setupFutureUsage,
              name: `${startCase(setupFutureUsage)} - ${
                SETUP_FUTURE_USAGE_LABEL[setupFutureUsage]
              }`
            }
          ]
        : [],
      placeholder: "Select future usage type",
      options: SETUP_FUTURE_USAGE_OPTIONS,
      isMulti: false,
      disabled: disableSetupFutureUsage,
      onChange: () => ({ value }) => {
        if (
          !get(
            touched,
            STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE
          )
        ) {
          setFieldTouched(
            STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE,
            true
          );
        }

        setTimeout(
          () =>
            setFieldValue(
              STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE,
              value
            ),
          0
        );
      },
      labelTransform: (value) => value,
      components: {
        Option: MultiselectOption
      }
    };

    return (
      <div className="flex flex-column w-100">
        <div className="flex flex-row items-center justify-between">
          <label
            className={classnames("f5 fw6 db", {
              "color-error": error
            })}
          >
            {ctxTitle}
          </label>
          <Toggle
            {...getToggleProps({
              active: Boolean(setupFutureUsage),
              /**
               * Ensure that only disabled if it's off
               * Don't want to get stuck with an 'on' setting
               */
              disabled: disableSetupFutureUsage && !setupFutureUsage,
              setFieldValue: (_, active) => {
                if (
                  !get(
                    touched,
                    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE
                  )
                ) {
                  setFieldTouched(
                    STATE_KEYS.SHORT_LINK
                      .PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE,
                    true
                  );
                }

                /**
                 * By default set to off_session
                 */
                setTimeout(() => {
                  if (active) {
                    setFieldValue(
                      STATE_KEYS.SHORT_LINK
                        .PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE,
                      SETUP_FUTURE_USAGE.OFF_SESSION
                    );
                  } else {
                    setValues(
                      omitDeep(
                        values,
                        STATE_KEYS.SHORT_LINK
                          .PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE
                      )
                    );
                  }
                }, 0);
              }
            })}
          />
        </div>
        {!setupFutureUsage && notice ? (
          <div className="f6 pt2 lh-copy">{notice}</div>
        ) : (
          <div className="f6 pt2 lh-copy">{SETUP_FUTURE_USAGE_COPY.INFO}</div>
        )}
        {setupFutureUsage && (
          <Fragment>
            <div className="flex flex-row justify-between items-center pt3">
              <label className="f6 fw6 db pb2">Future usage type</label>
            </div>
            <MultiSelect {...setupFutureUsageSelectProps} />
            {notice && <div className="f6 pt2 lh-copy">{notice}</div>}
          </Fragment>
        )}
        <div className="flex flex-column pt2">
          <div className="f6 lh-copy word-break">
            {SETUP_FUTURE_USAGE_COPY.FOOTER}
          </div>
          <div className="pt2">
            <ExternalLink
              href={STRIPE_DOCS_LINKS.PAYMENT_INTENTS_FUTURE_USAGE}
              copy="Learn more"
              customClasses={{
                container: "w-100 db",
                link:
                  "link flex flex-row items-center link__decoration--hover color-primary pointer w-100 word-break-all",
                copy: "f6 fw6 lh-title pr1"
              }}
            />
          </div>
        </div>
      </div>
    );
  }
};
export default SetupFutureUsage;
